import { useEffect, useRef, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { Button, Check, Field } from './ui'

/**
 * The button for report_song().
 *
 * The RPC has existed since migration 14 with full schema behind it — a reason
 * enum, a reporter FK that survives account deletion so deleting an account
 * can't unreport a song, and a partial unique index that quietly swallows a
 * second press. Nothing in the app ever called it. This is the caller.
 *
 * Three things the RPC decides, so this component doesn't:
 *   - You cannot report your own song. It raises, so the control is simply not
 *     rendered on a song you wrote rather than offering an action that fails.
 *   - Repeat reports return success. A reporter told their second press failed
 *     will press a third time.
 *   - Severity comes from the reason, server-side. Nothing here can set it.
 */

/**
 * Five reasons is at the edge of what belongs in one list, so they are ordered
 * by how often they are actually true rather than by the enum's order, and the
 * free-text box only appears once a reason is chosen — until then it is asking
 * someone to explain a thing they haven't picked yet.
 */
const REASONS = [
  ['offensive', 'Offensive content', 'Profanity, hate, threats, or something explicit'],
  ['copyright', "This isn't their song", "It's someone else's work, or a cover"],
  ['not_music', "This isn't music", "The recording isn't a song"],
  ['spam', 'Spam', 'Advertising, links, or using a song to reach people'],
  ['other', 'Something else', 'Tell us in a line'],
]

export default function ReportSong({ songId, title }) {
  const dlg = useRef(null)
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState(null)
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState(null)

  // showModal() rather than the open attribute: it's what puts the dialog in the
  // top layer, which is the only way it clears .screen's overflow and the
  // fixed tab bar without a z-index argument.
  useEffect(() => {
    const d = dlg.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])

  const close = () => {
    setOpen(false)
    // Let the dialog finish closing before the contents change underneath it.
    setTimeout(() => { setReason(null); setDetail(''); setErr(null); setDone(false) }, 150)
  }

  const send = async () => {
    setBusy(true); setErr(null)
    const { error } = await supabase.rpc('report_song', {
      p_song: songId, p_reason: reason, p_detail: detail.trim() || null,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setDone(true)
  }

  return (
    <>
      {/* Quiet on purpose. Listen and Learn are what this screen is for; a
          report is rare and must not be drawn as though it competes with them. */}
      <button className="btn-plain reportlink" onClick={() => setOpen(true)}>
        Report
      </button>

      <dialog ref={dlg} className="sheet" onClose={close}
              onCancel={(e) => { e.preventDefault(); close() }}>
        <div className="sheet-body">
          {done ? (
            <>
              <div className="done-ic"><Check size={26} w={2.4} /></div>
              <h2 className="q center">Reported.</h2>
              <p className="sub center">
                A moderator reads every one of these. You won't hear back about it,
                and the writer isn't told who reported them.
              </p>
              <Button className="btn btn-quiet" onClick={close}>Close</Button>
            </>
          ) : (
            <>
              <h2 className="q">Report this song.</h2>
              <p className="sub">
                {title ? <>“{title}”. </> : null}
                This goes to a moderator, not to the writer. Nothing happens to the
                song until a person has read it.
              </p>

              <div className="stack">
                {REASONS.map(([id, label, hint]) => (
                  <button key={id} className={`chip chip-wide${reason === id ? ' on' : ''}`}
                          onClick={() => setReason(id)}>
                    <span style={{ flex: 1 }}>{label}<small>{hint}</small></span>
                    {reason === id ? <Check /> : '○'}
                  </button>
                ))}
              </div>

              {/* Only once there's something to add detail to. */}
              {reason && (
                <div className="fieldgroup" style={{ marginTop: 18 }}>
                  <Field label={reason === 'other' ? 'What happened?' : 'Anything to add?'}>
                    <textarea className="ta" value={detail} maxLength={500}
                              onChange={(e) => setDetail(e.target.value)}
                              placeholder={reason === 'other'
                                ? 'A line is plenty.'
                                : 'Optional. A line is plenty.'} />
                  </Field>
                  <p className="small">
                    Goes to a moderator with the song. Not a message to the writer.
                  </p>
                </div>
              )}

              {err && <p className="err">{err}</p>}

              <div className="hr" />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button busy={busy}
                        disabled={!reason || (reason === 'other' && !detail.trim())}
                        onClick={send}>
                  Send report
                </Button>
                <Button className="btn btn-quiet" disabled={busy} onClick={close}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </dialog>
    </>
  )
}
